import { Controller } from "@hotwired/stimulus";

// Keeps the chart alive across a tick. A stream `replace` swaps the whole data frame, destroying the
// <canvas> so Chart.js replays its entry animation every few seconds. This intercepts the render and
// copies the incoming data-chart-* attributes onto the live elements instead, so Chart.js tweens.
//
// turbo:before-stream-render, NOT turbo:before-frame-render: a stream `replace` swaps the element
// directly and never performs a frame render.
export default class extends Controller {
  connect() {
    this.onBeforeStream = this.beforeStream.bind(this);
    // CAPTURE PHASE: Turbo listens on document too, and a bubble-phase listener runs after it has
    // already performed the swap, so preventDefault would come too late to save the canvas.
    document.addEventListener("turbo:before-stream-render", this.onBeforeStream, true);
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStream, true);
  }

  beforeStream(event) {
    const stream = event.detail?.newStream;
    // Only this card's own replace: the board broadcasts one stream per card.
    if (!stream || stream.getAttribute("target") !== this.element.id) return;
    if (stream.getAttribute("action") !== "replace") return;

    const incoming = stream.templateElement?.content;
    if (!incoming) return;

    const live = [...this.element.querySelectorAll('[data-controller~="chart"]')];
    const next = [...incoming.querySelectorAll('[data-controller~="chart"]')];
    // No charts, or a different chart count -- a genuinely different card. Let Turbo swap it wholesale.
    if (live.length === 0 || live.length !== next.length) return;

    next.forEach((node, i) => {
      // panels-value LAST: the panelsValueChanged redraw needs the other attributes already in place,
      // or it tweens to the new numbers using the old axes and colours.
      const names = node.getAttributeNames()
        .filter((name) => name.startsWith("data-chart-"))
        .sort((a, b) => (a === "data-chart-panels-value") - (b === "data-chart-panels-value"));
      for (const name of names) {
        const value = node.getAttribute(name);
        if (value !== null && live[i].getAttribute(name) !== value) live[i].setAttribute(name, value);
      }
    });

    // The charts are now current and everything else in a live chart card is chrome, so the swap that
    // would have re-mounted the canvas is cancelled.
    event.preventDefault();
  }
}
